import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormArray, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { sweetAlert } from 'src/app/utils/swal';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AssistService } from 'src/app/service/assist-service/assist.service';
import { PatientService } from 'src/app/service/patient-service/patient.service';
import { decryptField } from 'src/app/utils/decrypt-utils';
import * as moment from 'moment-timezone';

function budgetRangeValidator(group: FormGroup): ValidationErrors | null {
  const minControl = group.get('budget_min');
  const maxControl = group.get('budget_max');
  const min = minControl ? minControl.value : null;
  const max = maxControl ? maxControl.value : null;
  if (min !== null && max !== null && min !== '' && max !== '' && Number(min) >= Number(max)) {
    return { budgetRangeInvalid: true };
  }
  return null;
}

function dateComparisonValidator(group: FormGroup): ValidationErrors | null {
  const startControl = group.get('onboarding_date');
  const endControl = group.get('departure_date');
  const tourControl = group.get('tour_date');

  const startVal = startControl ? startControl.value : null;
  const endVal = endControl ? endControl.value : null;
  const tourVal = tourControl ? tourControl.value : null;

  const errors: any = {};
  const today = moment().startOf('day');

  if (startVal) {
    const start = moment(startVal).startOf('day');
    if (start.isBefore(today)) {
      errors.admissionStartPast = true;
    }
  }

  if (startVal && endVal) {
    const start = moment(startVal).startOf('day');
    const end = moment(endVal).startOf('day');
    if (!end.isAfter(start)) {
      errors.admissionDateInvalid = true;
    }
  }

  if (tourVal) {
    const tour = moment(tourVal).startOf('day');
    if (tour.isBefore(today)) {
      errors.tourDatePast = true;
    }
    if (startVal) {
      const start = moment(startVal).startOf('day');
      if (tour.isAfter(start)) {
        errors.tourDateAfterStart = true;
      }
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

function assistFormValidator(group: FormGroup): ValidationErrors | null {
  const budgetErrors = budgetRangeValidator(group);
  const dateErrors = dateComparisonValidator(group);
  
  if (!budgetErrors && !dateErrors) {
    return null;
  }
  
  return {
    ...budgetErrors,
    ...dateErrors
  };
}

@Component({
  selector: 'app-create-assist',
  templateUrl: './create-assist.component.html',
  styleUrls: ['./create-assist.component.css']
})
export class CreateAssistComponent implements OnInit, OnDestroy {
  assistForm: FormGroup;
  submitted = false;
  isRequestInprogress = false;
  patientId: string = '';
  intakeId: any;
  patientName: string = '';
  memberSummary: any = null;
  todayMinDate: Date = moment().startOf('day').toDate();

  hasAdditionalPreferredAddress = false;
  selectedPreferredStateId: string = '';
  selectedPreferredCityId: string = '';
  filteredPreferredCitiesList: any[] = [];
  availableZipcodes: string[] = [];
  selectedPreferredZipcodes: string[] = [];
  manualZipcodeInput: string = '';
  preferredLocationSubmitted = false;

  statesList: any[] = [];
  citiesList: any[] = [];
  filteredCitiesList: any[] = [];
  facilityTypes: any[] = [];
  accommodationTypes: any[] = [];
  isLoadingData = false;

  budgetRangeOptions = [
    'Monthly',
    'Daily'
  ];

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private assistService: AssistService,
    private patientService: PatientService
  ) { }

  ngOnInit() {
    this.isLoadingData = true;
    forkJoin([
      this.assistService.getStates(),
      this.assistService.getFacilityTypes(),
      this.assistService.getAccommodationTypes()
    ]).pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        ([states, facilityTypes, accTypes]) => {
          this.statesList = states || [];
          this.facilityTypes = facilityTypes || [];
          this.accommodationTypes = accTypes || [];

          this.assistService.getCities().pipe(takeUntil(componentDestroyed(this)))
            .subscribe(
              (cities) => {
                this.citiesList = cities || [];
                this.isLoadingData = false;

                // If query params state was loaded, filter cities
                const currentStateId = this.assistForm.get('state_id').value;
                if (currentStateId) {
                  this.onStateChange(currentStateId);
                }
              },
              (err) => {
                this.isLoadingData = false;
              }
            );
        },
        (err) => {
          this.isLoadingData = false;
        }
      );

    this.activatedRoute.queryParams
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(params => {
        this.patientId = params['patientId'] || '';
        this.patientName = params['patientName'] || '';
        this.intakeId = params['intakeId'] || '';
        // if (this.assistForm) {
        //   this.assistForm.patchValue({
        //     patient_id: this.patientId,
        //     intake_id: this.intakeId
        //   });
        // }
        if (this.intakeId) {
          this.getPatientDetails(this.intakeId);
        }
      });

       this.initForm();
  }

  initForm() {
    this.assistForm = this.fb.group({
      patient_id: [this.patientId],
      intake_id: [this.intakeId],
      state_id: ['', Validators.required],
      city_id: ['', Validators.required],
      facilitytype_id: ['', Validators.required],
      accommodationtype_id: ['', Validators.required],
      budgettype_id: ['', Validators.required],
      budget_min: ['', [Validators.required, Validators.min(0)]],
      budget_max: ['', [Validators.required, Validators.min(0)]],
      preAdmissionStartDate: [''],
      onboarding_date: ['', Validators.required],
      preAdmissionEndDate: [''],
      departure_date: ['', Validators.required],
      preTourDate: [''],
      tour_date: ['', Validators.required],
      preTourTime: [''],
      tour_time: ['', Validators.required],
      declaration_correct: [false],
      preferred_location: this.fb.array([])
    }, { validator: assistFormValidator });

    this.assistForm.get('preAdmissionStartDate').valueChanges
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(() => {
        this.assistForm.get('preTourDate').updateValueAndValidity();
        this.assistForm.get('preAdmissionEndDate').updateValueAndValidity();
      });
  }

  get preferredLocationsForm() {
    return this.assistForm.get('preferred_location') as FormArray;
  }

  get form() {
    return this.assistForm.controls;
  }

  onStateChange(stateValue: any) {
    if (!stateValue) {
      this.filteredCitiesList = [];
      this.assistForm.patchValue({ city_id: '' });
      return;
    }
    const stateId = Number(stateValue);
    this.filteredCitiesList = this.citiesList.filter(city => city.state_id === stateId);
    this.assistForm.patchValue({ city_id: '' });
  }

  onDateChange(evt: any, control: AbstractControl) {
    if (evt) {
      control.setValue(moment(evt).toISOString());
    }
  }

  onCancel() {
    this.router.navigateByUrl('/placement/patient');
  }

  toggleAdditionalPreferredAddress() {
    const control = this.assistForm.get('declaration_correct');
    if (this.hasAdditionalPreferredAddress) {
      control.setValidators([Validators.requiredTrue]);
    } else {
      control.clearValidators();
      control.setValue(false);
    }
    control.updateValueAndValidity();
  }

  onPreferredStateChange(stateValue: any) {
    if (!stateValue) {
      this.filteredPreferredCitiesList = [];
      this.selectedPreferredCityId = '';
      this.availableZipcodes = [];
      this.selectedPreferredZipcodes = [];
      this.manualZipcodeInput = '';
      return;
    }
    const stateId = Number(stateValue);
    this.filteredPreferredCitiesList = this.citiesList.filter(city => city.state_id === stateId);
    this.selectedPreferredCityId = '';
    this.availableZipcodes = [];
    this.selectedPreferredZipcodes = [];
    this.manualZipcodeInput = '';
  }

  onPreferredCityChange(cityValue: any) {
    const cityId = Number(cityValue);
    this.selectedPreferredZipcodes = [];
    this.manualZipcodeInput = '';

    if (!cityId) {
      this.availableZipcodes = [];
      return;
    }

    this.isLoadingData = true;
    this.assistForm.disable(); // disable all controls in form group while loading

    this.assistService.getZipcodesByCity(cityId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (response: any) => {
          this.isLoadingData = false;
          this.assistForm.enable();

          const data = (response && response.data !== undefined) ? response.data : response;
          let rawZipcodes = [];
          if (Array.isArray(data)) {
            rawZipcodes = data;
          } else if (data && Array.isArray(data.zipcodes)) {
            rawZipcodes = data.zipcodes;
          } else if (data && typeof data === 'object') {
            rawZipcodes = [data];
          } else if (typeof data === 'string') {
            rawZipcodes = data.split(',');
          }

          const parsedZips = rawZipcodes.map((z: any) => {
            if (typeof z === 'object' && z !== null) {
              return String(z.zipcode || z.zip_code || z.zipCode || z.zip || '');
            }
            return String(z);
          }).map((z: string) => z.trim()).filter(Boolean);

          parsedZips.forEach((zip: string) => {
            if (!this.selectedPreferredZipcodes.includes(zip)) {
              this.selectedPreferredZipcodes.push(zip);
            }
          });
        },
        (error) => {
          this.isLoadingData = false;
          this.assistForm.enable();
          console.error('Error fetching zipcodes:', error);
          sweetAlert('Error', error || 'Failed to fetch ZIP codes', 'warning', 'OK');
        }
      );
  }

  getPreferredCityName(): string {
    const cityId = Number(this.selectedPreferredCityId);
    const selectedCity = this.citiesList.find(c => c.id === cityId);
    return selectedCity ? selectedCity.name : '';
  }

  addZipcode(zip: string) {
    const trimmed = zip.trim();
    if (trimmed && !this.selectedPreferredZipcodes.includes(trimmed)) {
      this.selectedPreferredZipcodes.push(trimmed);
    }
  }

  addManualZipcode() {
    if (this.manualZipcodeInput) {
      this.addZipcode(this.manualZipcodeInput);
      this.manualZipcodeInput = '';
    }
  }

  removeSelectedZipcode(zip: string) {
    this.selectedPreferredZipcodes = this.selectedPreferredZipcodes.filter(z => z !== zip);
  }

  onZipcodeInputKeyDown(event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.manualZipcodeInput && this.selectedPreferredZipcodes.length > 0) {
      this.selectedPreferredZipcodes.pop();
    }
  }

  addPreferredLocation() {
    this.preferredLocationSubmitted = true;
    if (!this.selectedPreferredStateId || !this.selectedPreferredCityId) {
      return;
    }

    const selectedState = this.statesList.find(s => s.id === Number(this.selectedPreferredStateId));
    const selectedCity = this.citiesList.find(c => c.id === Number(this.selectedPreferredCityId));

    if (selectedState && selectedCity) {
      const isDuplicate = this.preferredLocationsForm.controls.some((ctrl: AbstractControl) => {
        const val = ctrl.value;
        return val.city_id === selectedCity.id && val.state_id === selectedState.id;
      });

      if (isDuplicate) {
        sweetAlert('Duplicate', 'This location has already been added.', 'warning', 'OK');
        return;
      }

      const locationGroup = this.fb.group({
        city_id: [selectedCity.id],
        city_name: [selectedCity.name],
        id: [this.generateUUID()],
        state_id: [selectedState.id],
        state_name: [selectedState.name],
        zipcodes: [this.selectedPreferredZipcodes.join(', ')]
      });

      this.preferredLocationsForm.push(locationGroup);

      this.preferredLocationSubmitted = false;
      this.selectedPreferredStateId = '';
      this.selectedPreferredCityId = '';
      this.filteredPreferredCitiesList = [];
      this.availableZipcodes = [];
      this.selectedPreferredZipcodes = [];
      this.manualZipcodeInput = '';
    }
  }

  removePreferredLocation(index: number) {
    this.preferredLocationsForm.removeAt(index);
  }

  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  saveAssist() {
    this.submitted = true;
    if (this.assistForm.invalid) {
      return;
    }

    if (this.hasAdditionalPreferredAddress && this.preferredLocationsForm.length === 0) {
      sweetAlert('Warning', 'Please add at least one preferred location or toggle off the additional preferred address options.', 'warning', 'OK');
      return;
    }

    this.isRequestInprogress = true;

    const formVal = this.assistForm.value;
    const onboardingDateFormatted = formVal.onboarding_date ? moment(formVal.onboarding_date).format('YYYY-MM-DD') : '';
    const departureDateFormatted = formVal.departure_date ? moment(formVal.departure_date).format('YYYY-MM-DD') : '';
    const patientId = formVal.patient_id;

    const queryParams = {
      onboarding_date: onboardingDateFormatted,
      departure_date: departureDateFormatted,
      patient_id: String(patientId)
    };

    this.assistService.checkDuplicate(queryParams)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          if (res) {
            const refNo = res.reference_id || '';
            const confirmMsg = `There is an existing intake broadcast with the selected admission dates. Ref no: ${refNo}. Are you sure you want to still proceed?`;
            sweetAlert('Duplicate Assist', confirmMsg, 'warning', 'Yes', 'No')
              .then((confirmResult: any) => {
                if (confirmResult && confirmResult.value) {
                  this.executeCreateAssist(formVal);
                } else {
                  this.isRequestInprogress = false;
                }
              });
          } else {
            this.executeCreateAssist(formVal);
          }
        },
        (error) => {
          this.isRequestInprogress = false;
          console.error('Error checking duplicate:', error);
          const errorMsg = error && typeof error === 'string' ? error : 'Failed to check duplicate assist.';
          sweetAlert('Error', errorMsg, 'warning', 'OK');
        }
      );
  }

  executeCreateAssist(formVal: any) {
    const selectedState = this.statesList.find(s => s.id === Number(formVal.state_id));
    const selectedCity = this.citiesList.find(c => c.id === Number(formVal.city_id));

    let preferredLocation = [];
    if (this.hasAdditionalPreferredAddress) {
      preferredLocation = this.preferredLocationsForm.value;
    } else {
      preferredLocation = selectedCity ? [{
        city_id: selectedCity.id,
        city_name: selectedCity.name,
        id: this.generateUUID(),
        state_id: selectedState ? selectedState.id : selectedCity.state_id,
        state_name: selectedState ? selectedState.name : '',
        zipcodes: ""
      }] : [];
    }

    let walkthroughDate = '';
    if (formVal.tour_date) {
      walkthroughDate = moment(formVal.tour_date).format('YYYY-MM-DD');
      if (formVal.tour_time) {
        walkthroughDate += ' ' + moment(formVal.tour_time).format('HH:mm');
      }
    }

    const payload = {
      accommodation_id: Number(formVal.accommodation_id) || 0,
      accommodationtype_id: Number(formVal.accommodationtype_id),
      budget_max: Number(formVal.budget_max),
      budget_min: Number(formVal.budget_min),
      budgettype_id: formVal.budgettype_id,
      city_id: Number(formVal.city_id),
      declaration_correct: formVal.declaration_correct !== undefined ? !!formVal.declaration_correct : true,
      departure_date: formVal.departure_date ? moment(formVal.departure_date).format('YYYY-MM-DD') : '',
      facility_id: Number(formVal.facility_id) || 0,
      facilitytype_id: Number(formVal.facilitytype_id),
      intake_id: Number(formVal.intake_id) || null,
      onboarding_date: formVal.onboarding_date ? moment(formVal.onboarding_date).format('YYYY-MM-DD') : '',
      patient_id: Number(formVal.patient_id),
      preferred_location: preferredLocation,
      state_id: Number(formVal.state_id),
      walkthrough_date: walkthroughDate
    };

    this.isRequestInprogress = true;

    this.assistService.createAssist(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isRequestInprogress = false;
          sweetAlert('Success', 'This patient intake has been sent to all care placement agents.', 'success', 'OK').then(() => {
            this.router.navigateByUrl('/placement/assists');
          });
        },
        (err) => {
          this.isRequestInprogress = false;
          sweetAlert('Error', err || 'Something went wrong', 'warning', 'OK');
        }
      );
  }

  getPatientDetails(id: string) {
    this.patientService.getIntakeById(id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (response: any) => {
          const intakeData = (response && response.data) ? response.data : response;
          if (intakeData) {
            console.log('intakeData', intakeData);
            const patientData = Array.isArray(intakeData.e_patients) 
              ? intakeData.e_patients[0] 
              : (intakeData.e_patients || {});
            
            const firstName = decryptField(patientData.first_name || patientData.firstName);
            const lastName = decryptField(patientData.last_name || patientData.lastName);
            const phone = decryptField(patientData.phone || patientData.contactNumber || patientData.cellPhone);
            const dob = decryptField(patientData.date_of_birth || patientData.dob);
            const refId = decryptField(patientData.reference_id);
            const refName = decryptField(patientData.reference_name);
            const refPhone = decryptField(patientData.reference_phone);
            
            // Map to the object expected by the summary template card
            this.memberSummary = {
              firstName: firstName || 'N/A',
              lastName: lastName || 'N/A',
              displayName: [firstName, lastName].filter(Boolean).join(' ') || 'N/A',
              clientId: patientData.client_id || patientData.clientId || 'N/A',
              email: patientData.email || 'N/A',
              contactNumber: phone || 'N/A',
              cellPhone: phone || 'N/A',
              gender: patientData.gender || 'N/A',
              dob: dob && moment(dob).isValid() ? moment(dob).toDate() : null,
              type: patientData.type || 'Regular',
              weight: intakeData.weight || patientData.weight || 'N/A',
              refId: refId || '—',
              refName: refName || '—',
              refPhone: refPhone || '—',
              homeAddress: {
                address: patientData.address || 
                         (patientData.homeAddress && patientData.homeAddress.address) || 
                         (patientData.e_clients && patientData.e_clients.address) || 
                         'N/A'
              }
            };
          }
        },
        (error) => {
          console.error('Error fetching patient details:', error);
        }
      );
  }

  ngOnDestroy() { }
}
